import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useEEGAnalysis, useConnectionState, useProcessedDataStore, useEEGMovingAverage } from '../../../stores/processedDataStore';
import { useDeviceStore } from '../../../stores/deviceStore';
import { useSensorContactStatus } from '../../../stores/systemStore';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';
import { getIndexGuide } from '../../../constants/indexGuides';
import { AlertTriangle, CheckCircle, XCircle, Clock } from 'lucide-react';
import { useTranslation } from "../../../locales";
import { useLanguageStore } from "../../../stores/languageStore";

// EEG 분석 지수 타입 정의 (Store 기반으로 변경됨)

const EEGIndexesChart: React.FC = () => {
  const { currentLanguage } = useLanguageStore();
  const t = useTranslation(currentLanguage);
  const { connectionState } = useDeviceStore();
  const eegAnalysis = useEEGAnalysis();
  const isConnected = useConnectionState();
  const { isSensorContacted, leadOffStatus } = useSensorContactStatus();
  const [currentTime, setCurrentTime] = useState(Date.now());
  
  // Store 기반 Moving Average 데이터 사용
  const eegMovingAverage = useEEGMovingAverage();

  // processedDataStore에서 EEG 인덱스 데이터 사용
  const eegIndices = eegAnalysis?.indices;
  
  // 실제 신호 품질 정보 확인 (ProcessedDataStore에서 가져오기)
  const signalQualityData = useProcessedDataStore(state => state.signalQuality);

  // processedDataStore에서 EEG SQI 데이터 가져오기
  const eegSQIData = useProcessedDataStore(state => state.sqiData.eegSQI);

  // 데이터 품질 상태 계산 (실제 SQI 기반)
  const dataQuality = useMemo(() => {
    const sensorOk = isSensorContacted;
    const bothChannelsOk = !leadOffStatus.fp1 && !leadOffStatus.fp2;
    
    // SQI 값 확인 (80% 이상을 양호로 판단)
    const sqiThreshold = 80;
    
    // 최신 SQI 값 확인
    const latestCh1SQI = eegSQIData?.ch1SQI?.[eegSQIData.ch1SQI.length - 1]?.value || 0;
    const latestCh2SQI = eegSQIData?.ch2SQI?.[eegSQIData.ch2SQI.length - 1]?.value || 0;
    const averageSQI = (latestCh1SQI + latestCh2SQI) / 2;
    const eegQualityOk = averageSQI >= sqiThreshold;
    
    // 전체 품질 = 센서 접촉 + 채널 품질 + SQI 품질
    const overall = sensorOk && bothChannelsOk && eegQualityOk;
    

    
    return {
      sensorContact: sensorOk,
      channelQuality: bothChannelsOk,
      eegQuality: eegQualityOk,
      averageSQI: averageSQI,
      overall: overall,
      fp1Status: !leadOffStatus.fp1,
      fp2Status: !leadOffStatus.fp2
    };
  }, [isSensorContacted, leadOffStatus, eegAnalysis, eegSQIData]);

  // Store 기반 EEG Moving Average 업데이트
  useEffect(() => {
    if (!eegIndices || !isConnected || !dataQuality.overall) {
      return;
    }

    // SQI 품질이 80% 이상인지 확인
    const sqiQuality = dataQuality.averageSQI >= 80;
    
    // Store의 updateEEGMovingAverage 액션 호출
    const updateEEGMovingAverage = useProcessedDataStore.getState().updateEEGMovingAverage;
    updateEEGMovingAverage(eegIndices, sqiQuality);
  }, [eegIndices, isConnected, dataQuality.overall, dataQuality.averageSQI]);

  // Store 기반 EEG 인덱스 데이터 사용
  const indexData = useMemo(() => {
    if (!eegIndices || !isConnected) {
      return {
        totalPower: 0,
        focusIndex: 0,
        relaxationIndex: 0,
        stressIndex: 0,
        hemisphericBalance: 0,
        cognitiveLoad: 0,
        emotionalStability: 0,
        attentionLevel: 0,
        meditationLevel: 0,
        hasData: false
      };
    }
    
    // 품질이 좋고 충분한 히스토리가 있으면 안정화된 값 사용, 아니면 원본 값 사용
    const useStabilized = dataQuality.overall && eegMovingAverage.history.focusIndex.length >= 10;
    const values = useStabilized ? eegMovingAverage.stabilizedValues : {
      totalPower: eegIndices.totalPower || 0,
      focusIndex: eegIndices.focusIndex || 0,
      relaxationIndex: eegIndices.relaxationIndex || 0,
      stressIndex: eegIndices.stressIndex || 0,
      hemisphericBalance: eegIndices.hemisphericBalance || 0,
      cognitiveLoad: eegIndices.cognitiveLoad || 0,
      emotionalStability: eegIndices.emotionalStability || 0,
      attentionLevel: ((eegIndices.focusIndex || 0) * 0.8 + (eegIndices.totalPower || 0) * 0.2),
      meditationLevel: ((eegIndices.relaxationIndex || 0) * 0.7 + (1 - (eegIndices.stressIndex || 0)) * 0.3)
    };
    
    return {
      ...values,
      hasData: true
    };
  }, [eegAnalysis, eegIndices, isConnected, connectionState, dataQuality, eegMovingAverage]);

  // 블루투스 데이터 청크가 들어올 때마다 즉시 업데이트
  useEffect(() => {
    setCurrentTime(Date.now());
  }, [eegAnalysis]);

  // 각 지표별 정상 범위 정의
  const getIndexStatus = (label: string, value: number) => {
    switch (label) {
      case '집중력 (Focus)':
        if (value < 1.8) return { status: 'low', color: 'text-yellow-400', message: t.visualizer.eegFocusLowMsg };
        if (value >= 2.4) return { status: 'high', color: 'text-slate-300', message: t.visualizer.eegFocusHighMsg };
        return { status: 'normal', color: 'text-green-400', message: t.visualizer.eegFocusNormalMsg };
      
      case '이완및긴장도 (Arousal)':
        if (value < 0.18) return { status: 'low', color: 'text-yellow-400', message: t.visualizer.eegArousalLowMsg };
        if (value > 0.22) return { status: 'high', color: 'text-slate-300', message: t.visualizer.eegArousalHighMsg };
        return { status: 'normal', color: 'text-green-400', message: t.visualizer.eegArousalNormalMsg };
      
      case '스트레스 (Stress)':
        if (value < 2.0) return { status: 'low', color: 'text-indigo-400', message: t.visualizer.eegStressVeryLowMsg };
        if (value < 3.0) return { status: 'low', color: 'text-green-400', message: t.visualizer.eegStressLowMsg };
        if (value < 4.0) return { status: 'normal', color: 'text-green-400', message: t.visualizer.eegStressNormalMsg };
        if (value < 5.0) return { status: 'high', color: 'text-slate-400', message: t.visualizer.eegStressHighMsg };
        return { status: 'high', color: 'text-slate-300', message: t.visualizer.eegStressVeryHighMsg };
      
      case '좌우뇌 균형':
        // NaN이나 Infinity 체크
        if (!isFinite(value) || isNaN(value)) {
          return { status: 'normal', color: 'text-muted-foreground', message: t.visualizer.eegBalanceMeasuringMsg };
        }
        if (value <= -0.1) return { status: 'low', color: 'text-indigo-400', message: t.visualizer.eegBalanceRightMsg };
        if (value >= 0.1) return { status: 'low', color: 'text-purple-400', message: t.visualizer.eegBalanceLeftMsg };
        return { status: 'normal', color: 'text-green-400', message: t.visualizer.eegBalanceNormalMsg };
      
      case '인지 부하':
        if (value < 0.3) return { status: 'low', color: 'text-yellow-400', message: t.visualizer.eegCognitiveLowMsg };
        if (value > 1.2) return { status: 'high', color: 'text-slate-300', message: t.visualizer.eegCognitiveOverloadMsg };
        if (value > 0.8) return { status: 'high', color: 'text-slate-300', message: t.visualizer.eegCognitiveHighMsg };
        return { status: 'normal', color: 'text-green-400', message: t.visualizer.eegCognitiveNormalMsg };
      
      case '정서안정성 (Valence)':
        if (value < 0.4) return { status: 'low', color: 'text-yellow-400', message: t.visualizer.eegValenceLowMsg };
        if (value > 0.8) return { status: 'high', color: 'text-slate-300', message: t.visualizer.eegValenceHighMsg };
        return { status: 'normal', color: 'text-green-400', message: t.visualizer.eegValenceNormalMsg };
      
      case '총 파워':
      case '신경활동성':
        if (value >= 1150) return { status: 'high', color: 'text-slate-300', message: t.visualizer.eegPowerHighMsg };
        if (value < 850) return { status: 'low', color: 'text-yellow-400', message: t.visualizer.eegPowerLowMsg };
        return { status: 'normal', color: 'text-green-400', message: t.visualizer.eegPowerNormalMsg };
      
      default:
        return { status: 'normal', color: 'text-green-400', message: t.visualizer.eegFocusNormalMsg };
    }
  };

  // 카드 라벨 표시 매핑 (i18n)
  const labelDisplayMap: Record<string, string> = {
    '이완및긴장도 (Arousal)': t.visualizer.eegArousalLabel,
    '정서안정성 (Valence)': t.visualizer.eegValenceLabel,
    '집중력 (Focus)': t.visualizer.eegFocusLabel,
    '스트레스 (Stress)': t.visualizer.eegStressLabel,
    '신경활동성': t.visualizer.eegTotalPowerLabel,
    '인지 부하': t.visualizer.eegCognitiveLoadLabel,
    '좌우뇌 균형': t.visualizer.eegBalanceLabel,
  };

  // 카드 데이터 정의 (새로운 배치 순서)
  const cardData = [
    // 1행: 이완및긴장도, 정서안정성, 집중력, 스트레스
    {
      label: '이완및긴장도 (Arousal)',
      value: indexData.relaxationIndex,
      unit: '',
      color: 'bg-green-500/10',
      description: t.visualizer.alphaBand + ' relaxation',
      historyLength: eegMovingAverage.history.relaxationIndex.length
    },
    {
      label: '정서안정성 (Valence)',
      value: indexData.emotionalStability,
      unit: '',
      color: 'bg-pink-500',
      description: t.visualizer.gammaBand + ' emotional stability',
      historyLength: eegMovingAverage.history.emotionalStability.length
    },
    {
      label: '집중력 (Focus)',
      value: indexData.focusIndex,
      unit: '',
      color: 'bg-indigo-500',
      description: t.visualizer.betaBand + ' focus index',
      historyLength: eegMovingAverage.history.focusIndex.length
    },
    {
      label: '스트레스 (Stress)',
      value: indexData.stressIndex,
      unit: '',
      color: 'bg-red-500',
      description: t.visualizer.eegStressDesc,
      historyLength: eegMovingAverage.history.stressIndex.length
    },
    // 2행: 신경활동성, 인지부하, 좌우뇌균형
    {
      label: '신경활동성',
      value: indexData.totalPower,
      unit: 'μV²',
      color: 'bg-purple-500',
      description: t.visualizer.eegTotalPowerDesc,
      historyLength: eegMovingAverage.history.totalPower.length
    },
    {
      label: '인지 부하',
      value: indexData.cognitiveLoad,
      unit: '',
      color: 'bg-yellow-500/10',
      description: t.visualizer.eegCognitiveLoadDesc,
      historyLength: eegMovingAverage.history.cognitiveLoad.length
    },
    {
      label: '좌우뇌 균형',
      value: indexData.hemisphericBalance,
      unit: '',
      color: 'bg-cyan-500',
      description: t.visualizer.eegBalanceDesc,
      historyLength: eegMovingAverage.history.hemisphericBalance.length
    }
  ];

  // 카드 렌더링 함수
  const renderCard = (card: any) => {
    const isStabilized = dataQuality.overall && card.historyLength >= 10;
    const cardOpacity = dataQuality.overall ? 'opacity-100' : 'opacity-60';
    
    // 상태별 색상과 설명 가져오기 (좌우뇌 균형은 0값도 유효)
    const isValidValue = card.value !== null && card.value !== undefined && 
      (card.label === '좌우뇌 균형' ? 
        isFinite(card.value) && !isNaN(card.value) && Math.abs(card.value) <= 1 : // -1 ~ 1 범위 체크
        card.value > 0);
    
    const indexStatus = isValidValue
      ? getIndexStatus(card.label, card.value) 
      : { status: 'normal', color: 'text-muted-foreground', message: t.visualizer.eegIndexNoData };
    
    // 상태별 마커 색상 결정
    const getMarkerColor = () => {
      if (!isValidValue) {
        return 'bg-muted'; // 데이터 없음
      }
      switch (indexStatus.status) {
        case 'low': return 'bg-yellow-500/10';
        case 'high': return 'bg-slate-400';
        case 'normal': return 'bg-green-500/10';
        default: return 'bg-muted';
      }
    };
    
    return (
      <TooltipProvider key={card.label}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className={`bg-white/[0.03] border border-white/[0.06] rounded-lg p-4 hover:bg-white/[0.05] transition-colors ${cardOpacity}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${getMarkerColor()}`} />
                  <span className="text-sm font-medium text-foreground">{labelDisplayMap[card.label] || card.label}</span>
                  {/* 안정화 상태 표시 */}
                  {isStabilized && (
                    <div className="flex items-center gap-1 px-1.5 py-0.5 bg-indigo-900/30 text-indigo-400 border border-indigo-500/30 rounded text-xs">
                      <CheckCircle className="w-2.5 h-2.5" strokeWidth={1.5} />
                      <span className="text-xs">{t.visualizer.stabilizing}</span>
                    </div>
                  )}
                </div>
                {/* 품질 상태 표시 */}
                {!dataQuality.overall && (
                  <Clock className="w-3 h-3 text-muted-foreground" strokeWidth={1.5} />
                )}
              </div>
              <div className="text-2xl font-bold text-white mb-1">
                {isValidValue
                  ? card.value.toFixed(2) 
                  : '--'
                }
                <span className="text-sm text-muted-foreground ml-1">{card.unit}</span>
              </div>
              {/* 상태 설명 */}
              <div className={`text-xs font-medium mb-1 ${indexStatus.color}`}>
                {indexStatus.message}
              </div>
              {/* 히스토리 정보 */}
              {isStabilized && (
                <div className="text-xs text-muted-foreground">
                  {card.historyLength}{t.visualizer.eegSampleAvg}
                </div>
              )}
            </div>
          </TooltipTrigger>
          <TooltipContent className="bg-slate-900 border border-slate-700 shadow-xl">
            <div className="max-w-lg p-4 bg-card rounded-lg">
              <div 
                className="text-sm leading-relaxed space-y-2 [&>strong]:text-indigo-400 [&>strong]:font-semibold [&>br]:block [&>br]:mb-1"
                style={{
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
                }}
                dangerouslySetInnerHTML={{ 
                  __html: (getIndexGuide(card.label, currentLanguage === 'ko' ? 'ko' : 'en') || `<strong>${card.label}</strong><br/>${t.visualizer.indexDetailPreparing}`)
                    .replace(/<strong>/g, '<strong class="text-indigo-400 font-semibold">')
                    .replace(new RegExp(t.visualizer.tooltipFormula + ':', 'g'), '<span class="text-green-300 font-medium">' + t.visualizer.tooltipFormula + ':</span>')
                    .replace(new RegExp(t.visualizer.tooltipNormalRange + ':', 'g'), '<span class="text-yellow-300 font-medium">' + t.visualizer.tooltipNormalRange + ':</span>')
                    .replace(new RegExp(t.visualizer.tooltipInterpretation + ':', 'g'), '<span class="text-orange-300 font-medium">' + t.visualizer.tooltipInterpretation + ':</span>')
                    .replace(new RegExp(t.visualizer.tooltipReference + ':', 'g'), '<span class="text-purple-300 font-medium">' + t.visualizer.tooltipReference + ':</span>')
                    .replace(new RegExp(t.visualizer.tooltipDescription + ':', 'g'), '<span class="text-cyan-300 font-medium">' + t.visualizer.tooltipDescription + ':</span>')
                }}
              />
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  // 연결되지 않았거나 데이터가 없는 경우 메시지 표시
  if (!isConnected || !indexData.hasData) {
    return (
      <div className="w-full space-y-6">
      <div className="w-full h-80 flex items-center justify-center">
        <div className="text-center space-y-4">
          
          <div className="text-lg text-foreground">
            {t.visualizer.noGraphData}
          </div>
          <div className="text-sm text-muted-foreground">
            {t.visualizer.connectDevice}
          </div>
          <div className="text-xs text-muted-foreground mt-2">
            {t.visualizer.eegConnectToView}
          </div>
          <div className="text-xs text-muted-foreground mt-3 p-2 bg-card rounded">
              {t.visualizer.eegAnalyzing}
            </div>
          </div>
        </div>
      </div>
    );
  }

    return (
    <div className="w-full space-y-6">

      {/* 센서 접촉 상태 경고 */}
      {isConnected && !isSensorContacted && (
        <div className="mb-4 p-3 bg-red-900/20 border border-red-500/30 rounded-lg flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-red-400" strokeWidth={1.5} />
          <div className="text-red-300">
            <div className="font-medium">{t.visualizer.poorContactDetection}</div>
            <div className="text-sm">
              FP1: {leadOffStatus.fp1 ? t.visualizer.contactPoor : t.visualizer.contactNormal}, 
              FP2: {leadOffStatus.fp2 ? t.visualizer.contactPoor : t.visualizer.contactNormal} - 
              {t.visualizer.eegAccuracyDegrade}
          </div>
          </div>
        </div>
      )}

      {/* 타이틀과 연결 상태 */}
      <div className="flex items-center justify-between">
        <h3 className="lb-h3">{t.visualizer.eegAnalysisIndex}</h3>
        {(!isConnected || connectionState.status !== 'connected') && (
          <span className="text-sm text-muted-foreground">
            {t.visualizer.eegDeviceRequired}
          </span>
        )}
      </div>

      {/* EEG 지수 카드들 - 위에 4개, 아래 3개 */}
      <div className="space-y-4">
        {/* 상단 4개 카드 */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {cardData.slice(0, 4).map((card) => renderCard(card))}
        </div>
        
        {/* 하단 3개 카드 */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {cardData.slice(4, 7).map((card) => renderCard(card))}
        </div>
      </div>

      {/* 비의료기기 면책 문구 — 지표가 임상 진단으로 오인되지 않도록 상시 표시 */}
      <p className="text-xs text-muted-foreground">{t.visualizer.indexDisclaimer}</p>
    </div>
  );
};

export default EEGIndexesChart; 